/**
 * Maps internal error codes to user-friendly display strings.
 * Components should set error state to one of these codes rather than
 * exposing raw API messages or exception text to users.
 */

export const ERROR_MESSAGES: Record<string, { title: string; detail: string }> = {
  fetch_failed: {
    title: 'Unable to load data',
    detail:
      'Something went wrong while retrieving your data. Please try again. If the problem continues, contact your administrator.'
  },
  network_error: {
    title: 'Network error',
    detail:
      'Could not reach the server. Please check your connection and try again.'
  },
  unauthorized: {
    title: 'Session expired',
    detail: 'Your session has expired. Please log in again.'
  },
  forbidden: {
    title: 'Access denied',
    detail:
      'Your account is authenticated but does not currently have access to this data. Try logging in again. If the issue continues, contact your administrator.'
  },
  not_found: {
    title: 'Not found',
    detail: 'The requested item could not be found. It may have been deleted or moved.'
  },
  create_failed: {
    title: 'Unable to save',
    detail: 'Your changes could not be saved. Please try again or contact your administrator.'
  },
  update_failed: {
    title: 'Unable to update',
    detail: 'The update could not be completed. Please try again or contact your administrator.'
  },
  delete_failed: {
    title: 'Unable to delete',
    detail: 'The item could not be deleted. Please try again or contact your administrator.'
  },
  logs_failed: {
    title: 'Unable to load logs',
    detail:
      'Migration logs could not be retrieved. Please try again or contact your administrator.'
  },
  comparison_failed: {
    title: 'Unable to load comparison',
    detail:
      'The snapshot comparison could not be loaded. Please try again or contact your administrator.'
  },
  resources_failed: {
    title: 'Unable to load resources',
    detail:
      'Resources could not be retrieved from this organization. Please try again or contact your administrator.'
  },
  organizations_failed: {
    title: 'Unable to load organizations',
    detail:
      'Your organization list could not be retrieved. Please try again or contact your administrator.'
  }
} as const;

export type ErrorCode = keyof typeof ERROR_MESSAGES;

/** Returns user-friendly title/detail for an error code, falling back gracefully for unknown codes.
 * If the code is not recognized and looks like a complete user-facing error message,
 * returns it directly as the detail.
 */
export function getErrorMessage(code: string): { title: string; detail: string } {
  // If it's a known error code, return the mapped message
  if (ERROR_MESSAGES[code]) {
    return ERROR_MESSAGES[code];
  }

  // If it looks like a complete error message (contains spaces and is sentence-like),
  // treat it as a literal message and display it
  if (code.includes(' ') && code.length > 20) {
    return {
      title: 'Unable to load data',
      detail: code
    };
  }

  // Otherwise, use generic fallback
  return {
    title: 'Something went wrong',
    detail:
      'An unexpected error occurred. Please try again. If the problem continues, contact your administrator.'
  };
}
